import { z } from 'zod';
import { CameraStatus, EventSource } from './enums.js';
/**
 * The vision -> event-processor contract, carried on the Redis stream
 * `stream:detections`.
 *
 * The vision service is deliberately dumb about business meaning: it reports
 * what it saw, never what it means. Severity, rules and alerting live entirely
 * downstream in the event processor.
 */
/** Bounding box in normalised frame coordinates so it survives resolution changes. */
export const BoundingBox = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().min(0).max(1),
    h: z.number().min(0).max(1),
});
export const DetectionMessageKind = z.enum([
    'TRACK_STARTED',
    'TRACK_UPDATED',
    'TRACK_ENDED',
    'ZONE_STATE',
    'LINE_CROSS',
    'CAMERA_STATE',
    'HEARTBEAT',
]);
/**
 * An anonymous track. `trackId` is an integer scoped to one camera and one
 * stream session — it is reset whenever the stream reconnects, is never stored
 * as an identity, and is never correlated across cameras. There is no
 * biometric, facial or demographic data anywhere in this payload, by design.
 */
export const TrackPayload = z.object({
    trackId: z.number().int().nonnegative(),
    zoneId: z.string().uuid().nullable(),
    confidence: z.number().min(0).max(1),
    bbox: BoundingBox,
    /** Seconds the track has been continuously visible. */
    dwellSeconds: z.number().nonnegative().default(0),
});
export const ZoneStatePayload = z.object({
    zoneId: z.string().uuid(),
    occupancy: z.number().int().nonnegative(),
    /** Track ids currently inside the zone — anonymous, transient. */
    trackIds: z.array(z.number().int().nonnegative()).default([]),
    /** Longest continuous dwell inside the zone, in seconds. */
    maxDwellSeconds: z.number().nonnegative().default(0),
});
export const LineCrossPayload = z.object({
    lineId: z.string().uuid(),
    zoneId: z.string().uuid().nullable(),
    trackId: z.number().int().nonnegative(),
    direction: z.enum(['IN', 'OUT']),
    confidence: z.number().min(0).max(1),
});
export const CameraStatePayload = z.object({
    status: CameraStatus,
    previousStatus: CameraStatus.nullable(),
    fps: z.number().nonnegative().nullable(),
    reason: z.string().max(500).nullable(),
    consecutiveFailures: z.number().int().nonnegative().default(0),
});
export const HeartbeatPayload = z.object({
    fps: z.number().nonnegative(),
    inferenceMs: z.number().nonnegative(),
    decodeMs: z.number().nonnegative(),
    framesSkippedByMotionGate: z.number().int().nonnegative().default(0),
    queueDepth: z.number().int().nonnegative().default(0),
});
export const DetectionMessage = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('TRACK_STARTED'), payload: TrackPayload }),
    z.object({ kind: z.literal('TRACK_UPDATED'), payload: TrackPayload }),
    z.object({ kind: z.literal('TRACK_ENDED'), payload: TrackPayload }),
    z.object({ kind: z.literal('ZONE_STATE'), payload: ZoneStatePayload }),
    z.object({ kind: z.literal('LINE_CROSS'), payload: LineCrossPayload }),
    z.object({ kind: z.literal('CAMERA_STATE'), payload: CameraStatePayload }),
    z.object({ kind: z.literal('HEARTBEAT'), payload: HeartbeatPayload }),
]);
/** Envelope written to `stream:detections`. `v` guards future schema changes. */
export const DetectionEnvelope = z
    .object({
    v: z.literal(1),
    cameraId: z.string().uuid(),
    /** ISO-8601 UTC, produced from the frame timestamp, not from wall clock. */
    ts: z.string().datetime(),
    source: EventSource.default('VISION'),
    correlationId: z.string().min(8).max(64),
    snapshotPath: z.string().max(512).nullable().default(null),
})
    .and(DetectionMessage);
export const DETECTION_STREAM = 'stream:detections';
export const DETECTION_CONSUMER_GROUP = 'cg:event-processor';
/**
 * Detection stream is capped: it is a transport, not a store of record. Anything
 * that matters has already been persisted by the event processor by the time it
 * would be trimmed.
 */
export const DETECTION_STREAM_MAXLEN = 100_000;
//# sourceMappingURL=detections.js.map